import {
  Box,
  TextField,
  Chip,
  CircularProgress,
  Autocomplete,
  InputAdornment,
  IconButton,
} from '@mui/material'
import { ArrowDropDown } from '@mui/icons-material'
import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import {
  useCreateLabel,
  useLabelsByProject,
  useTestCaseLabels,
  useAssignLabelToTestCase,
  useUnassignLabelFromTestCase,
} from './hooks/useLabels'

import styles from './styles.module.scss'
import { labelKeys } from './constants/labelKeys'

const LabelsSelect = ({ projectId, testCaseId }) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('')
  const queryClient = useQueryClient()

  const { data: assignedLabels = [] } = useTestCaseLabels(testCaseId)
  const { data: allLabels = [], isLoading: isLoadingLabels } = useLabelsByProject(projectId)

  const assignMutation = useAssignLabelToTestCase(testCaseId)
  const unassignMutation = useUnassignLabelFromTestCase(testCaseId)
  const createLabelMutation = useCreateLabel({
    onSuccess: () => {
      queryClient.invalidateQueries([labelKeys.projectLabels, projectId])
    },
  })

  const selectedLabels = assignedLabels.map((label) => label.value)

  const getFilteredOptions = () => {
    const input = inputValue.trim().toLowerCase()

    let options = input === ''
      ? allLabels.map((l) => l.value)
      : allLabels
        .filter((l) => l.value.toLowerCase().includes(input))
        .map((l) => l.value)

    options = options.filter((value) => !selectedLabels.includes(value))

    if (
      input &&
      !allLabels.some((l) => l.value.toLowerCase() === input)
    ) {
      options.push(`${inputValue} (New Label)`)
    }

    return options
  }

  const assignLabel = async (labelId) => {
    try {
      await assignMutation.mutateAsync(labelId)
      queryClient.invalidateQueries([labelKeys.testCaseLabels, testCaseId])
    } catch (err) {
      console.error('Assign failed:', err)
    }
  }

  const unassignLabel = async (labelId) => {
    try {
      await unassignMutation.mutateAsync(labelId)
      queryClient.invalidateQueries([labelKeys.testCaseLabels, testCaseId])
    } catch (err) {
      console.error('Unassign failed:', err)
    }
  }

  const createLabel = async (value) => {
    try {
      const label = await createLabelMutation.mutateAsync({
        project_id: projectId,
        value,
      })
      await queryClient.invalidateQueries([labelKeys.projectLabels, projectId])
      return label
    } catch (err) {
      console.error('Label creation failed:', err)
      return null
    }
  }

  const handleLabelInput = async (value, isNew = false) => {
    if (selectedLabels.includes(value)) return

    let label = allLabels.find((l) => l.value === value)

    if (!label && isNew) {
      label = await createLabel(value)
      if (!label) return
    }

    if (label) {
      await assignLabel(label.id)
    }
  }

  const handleChange = async (_, newValues) => {
    const lastInput = newValues[newValues.length - 1]

    const removed = selectedLabels.find((label) => !newValues.includes(label))
    if (removed) {
      const labelToRemove = allLabels.find((l) => l.value === removed)
      if (labelToRemove) {
        await unassignLabel(labelToRemove.id)
      }
      return
    }

    let value = lastInput
    let isNew = false

    if (lastInput.endsWith(' (New Label)')) {
      value = lastInput.replace(' (New Label)', '')
      isNew = true
    }

    await handleLabelInput(value, isNew)
    setInputValue('')
  }

  const handleKeyDown = async (e) => {
    if (!open) setOpen(true);
    if (e.key !== 'Enter') return

    const trimmed = inputValue.trim()
    if (!trimmed || selectedLabels.includes(trimmed)) return

    e.preventDefault()
    await handleLabelInput(trimmed, true)
    setInputValue('')
  }

  const handleDelete = async (labelValue) => {
    const label = assignedLabels.find((l) => l.value === labelValue)
    if (label) {
      await unassignLabel(label.id)
    }
  }

  return (
    <Box className={styles.labelsMainContainer}>
      <Autocomplete
        multiple
        freeSolo
        disableClearable
        open={open}
        setOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        options={getFilteredOptions()}
        value={selectedLabels}
        onChange={handleChange}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        inputValue={inputValue}
        onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
        loading={isLoadingLabels}
        className={styles.detailsAccordionSelect}
        onKeyDown={handleKeyDown}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Labels"
            variant="outlined"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoadingLabels ? <CircularProgress size={20} /> : null}
                  <InputAdornment position="end">
                    <IconButton
                      tabIndex={-1}
                      edge="end"
                      disableRipple
                      onClick={() => setOpen((state) => !state)}
                    >
                      <ArrowDropDown />
                    </IconButton>
                  </InputAdornment>
                </>
              ),
            }}
          />
        )}
        renderTags={(selected, getTagProps) =>
          selected.map((option, index) => (
            <Chip
              className={styles.detailsAccordionSelectChip}
              label={option}
              {...getTagProps({ index })}
              key={option}
              onDelete={() => handleDelete(option)}
            />
          ))
        }
      />
    </Box>
  )
}

export default LabelsSelect
